import React, { useState, useEffect } from 'react';
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Chip,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { CloudOff, CloudSync } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getIconComponent } from '../hooks/useGetIconComponent';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../context/AuthContext/AuthContext';

interface SidebarListProps {
  items: any[];
  open: boolean;
  isMobile: boolean;
  handleDrawerClose: () => void;
  isDarkMode: boolean;
}

const SidebarList: React.FC<SidebarListProps> = ({
  items,
  open,
  isMobile,
  handleDrawerClose,
  isDarkMode,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['sidebar']);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const { permissions, profile } = useAuth();

  const isLinkActive = (link: string) => {
    const [path, query] = link.split('?');
    if (query) {
      return location.pathname === path && location.search === `?${query}`;
    }
    return location.pathname === link && !location.search;
  };

  const isGroupActive = (children: any[]) =>
    children.some((child) => isLinkActive(child.link));

  // Auto-expand group if a child route is currently active
  useEffect(() => {
    items.forEach((item) => {
      if (item.children && isGroupActive(item.children)) {
        setOpenGroups((prev) => ({ ...prev, [item.name]: true }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const handleLinkClick = (link: string) => {
    if (isMobile) {
      setLoading(true);
      handleDrawerClose();
      setTimeout(() => {
        setLoading(false);
        navigate(link);
      }, 300);
    } else {
      navigate(link);
    }
  };

  const handleGroupToggle = (name: string) => {
    setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const filteredItems = items.filter((item) => {
    if (item.permission) {
      if (!permissions.includes(item.permission)) {
        return false;
      }
    }
    if (item.requiresActiveChallenge) {
      if (!profile?.hasActiveChallenge) {
        return false;
      }
    }
    if (item.requiresActiveFundedChallenge) {
      if (!profile?.hasActiveFundedChallenge) {
        return false;
      }
    }
    return true;
  });

  const activeItemSx = {
    borderRadius: '8px',
    color: '#CCD4FF',
    backgroundColor: '#11121766',
    border: '1px solid #FFFFFF1A',
  };

  const inactiveItemSx = {
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: 'none',
  };

  return (
    <List sx={{ margin: '10px 0px', marginTop: open ? '10px' : '80px', paddingBottom: '40px' }}>
      {filteredItems.map((item) => {
        const IconComponent = getIconComponent(
          item.icon,
          isDarkMode,
          location.pathname,
        );

        // --- Collapsible group ---
        if (item.children) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const _isActive = isGroupActive(item.children);
          const isExpanded = !!openGroups[item.name];

          return (
            <React.Fragment key={item.name}>
              <Tooltip
                title={t(item.name)}
                placement="right"
                disableHoverListener={open}
              >
                <ListItem
                  sx={{
                    padding: '0px 16px',
                    display: 'block',
                    '& .MuiListItemButton-root': {
                      ...inactiveItemSx,
                    },
                    '& .MuiListItemButton-root:hover': {
                      backgroundColor: '#FFFFFF1A',
                    },
                  }}
                >
                  <ListItemButton
                    onClick={() => {
                      if (open) {
                        handleGroupToggle(item.name);
                      } else {
                        // When sidebar is collapsed, navigate to first child
                        handleLinkClick(item.children[0].link);
                      }
                    }}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      padding: '16px 24px',
                      position: 'relative',
                      '&:hover .MuiListItemIcon-root': {
                        transform: 'scale(1.05)',
                      },
                      '&:active .MuiListItemIcon-root': {
                        transform: 'scale(0.85)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isDarkMode ? '#6E83FF' : '#333333',
                        justifyContent: open ? 'initial' : 'center',
                        px: open ? 0 : 2.5,
                        transition: 'transform 0.15s ease',
                      }}
                    >
                      {IconComponent}
                    </ListItemIcon>
                    <ListItemText
                      primary={t(item.name)}
                      sx={{ display: open ? 'block' : 'none', flex: 1 }}
                    />
                    {open && (isExpanded ? <ExpandLess sx={{ position: 'absolute', right: 8 }} /> : <ExpandMore sx={{ position: 'absolute', right: 8 }} />)}
                  </ListItemButton>
                </ListItem>
              </Tooltip>

              <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {item.children.map((child: any) => {
                    const childActive = isLinkActive(child.link);
                    return (
                      <ListItem
                        key={child.link}
                        sx={{
                          padding: '0px 16px',
                          display: 'block',
                          '& .MuiListItemButton-root': {
                            ...(childActive ? activeItemSx : inactiveItemSx),
                          },
                          '& .MuiListItemButton-root:hover': {
                            backgroundColor: childActive ? '#11121766' : '#FFFFFF1A',
                          },
                        }}
                      >
                        <ListItemButton
                          onClick={() => handleLinkClick(child.link)}
                          sx={{
                            minHeight: 40,
                            padding: '10px 24px 10px 52px',
                            borderRadius: '8px',
                          }}
                        >
                          <ListItemText
                            primary={t(child.name)}
                            sx={{
                              '& .MuiListItemText-primary': {
                                fontSize: '0.875rem',
                                color: childActive ? '#CCD4FF' : 'inherit',
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          );
        }

        // --- Regular item ---
        return (
          <Tooltip
            title={t(item.name)}
            placement="right"
            key={item.name}
            disableHoverListener={open ? true : false}
          >
            <ListItem
              sx={{
                padding: '0px 16px',
                display: 'block',
                '& .MuiListItemButton-root': {
                  borderRadius: '8px',
                  ...(isLinkActive(item.link) && {
                    color: '#CCD4FF',
                  }),
                  backgroundColor: isLinkActive(item.link)
                    ? '#11121766'
                    : 'transparent',
                  border: isLinkActive(item.link)
                    ? '1px solid #FFFFFF1A'
                    : 'none',
                },
                '& .MuiListItemButton-root:hover': {
                  backgroundColor: isLinkActive(item.link)
                    ? '#11121766'
                    : '#FFFFFF1A',
                },
              }}
            >
              <ListItemButton
                onClick={() => handleLinkClick(item.link)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  padding: '16px 24px',
                  '&:hover .MuiListItemIcon-root': {
                    transform: 'scale(1.05)',
                  },
                  '&:active .MuiListItemIcon-root': {
                    transform: 'scale(0.85)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isLinkActive(item.link)
                      ? '#6E83FF'
                      : isDarkMode
                      ? '#6E83FF'
                      : '#333333',
                    justifyContent: open ? 'initial' : 'center',
                    px: open ? 0 : 2.5,
                    transition: 'transform 0.15s ease',
                  }}
                >
                  {IconComponent}
                </ListItemIcon>
                <ListItemText
                  primary={t(item.name)}
                  sx={{ display: open ? 'block' : 'none' }}
                />
                {item.new && open && (
                  <Chip
                    label={'NEW'}
                    sx={{
                      backgroundColor: isLinkActive(item.link)
                        ? 'white'
                        : '#e73d44',
                      color: isLinkActive(item.link) ? '#333333' : '#FFFFFF',
                    }}
                  />
                )}
                {item.sync && open && (
                  profile?.rfxAccountSynced ? (
                    <Chip
                      icon={<CloudSync size={14} color="#4EDC6B" />}
                      label="Sync"
                      size="small"
                      sx={{ backgroundColor: '#4EDC6B33', color: '#4EDC6B', fontWeight: 600, fontSize: '0.7rem', height: 22, border: '1px solid #FFFFFF1A' }}
                    />
                  ) : (
                    <Chip
                      icon={<CloudOff size={14} color="#FF6B6B" />}
                      label="Sync"
                      size="small"
                      sx={{ backgroundColor: '#FF6B6B33', color: '#FF6B6B', fontWeight: 600, fontSize: '0.7rem', height: 22, border: '1px solid #FFFFFF1A' }}
                    />
                  )
                )}
              </ListItemButton>
            </ListItem>
          </Tooltip>
        );
      })}
    </List>
  );
};

export default SidebarList;
